import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useProject } from "@/context/ProjectContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  SquaresFour,
  ClipboardText,
  Plus,
  UsersThree,
  SignOut,
  Buildings,
  ArrowSquareOut,
  HardHat,
  ShieldWarning,
  Package,
  ChartLineUp,
  Swap,
  Tag,
  ClockCounterClockwise,
  Receipt,
  Scales,
  LinkSimple,
} from "@phosphor-icons/react";

const navSections = [
  {
    label: "Overview",
    items: [
      { to: "/", label: "Dashboard", icon: SquaresFour, end: true },
      { to: "/boq", label: "Bill of Quantities", icon: ClipboardText },
      { to: "/approval-summary", label: "Approval Summary", icon: ChartLineUp },
    ],
  },
  {
    label: "Purchase",
    items: [
      { to: "/pos", label: "Material POs", icon: ClipboardText },
      { to: "/pos/new", label: "Raise Material PO", icon: Plus, writeOnly: true },
      { to: "/pos/bulk-map", label: "Bulk Map POs → BOQ", icon: LinkSimple, adminOnly: true },
      { to: "/contracts", label: "Contract POs", icon: HardHat },
      { to: "/contracts/new", label: "Raise Contract PO", icon: Plus, writeOnly: true },
      { to: "/invoices", label: "Invoices", icon: Receipt },
    ],
  },
  {
    label: "Outward",
    items: [
      { to: "/outward", label: "Outward Material", icon: ArrowSquareOut },
      { to: "/outward/new", label: "Issue Material", icon: Plus, writeOnly: true },
    ],
  },
  {
    label: "Transfers",
    items: [
      { to: "/transfers", label: "Site Transfers", icon: Swap },
      { to: "/transfers/new", label: "New Transfer", icon: Plus, writeOnly: true },
    ],
  },
  {
    label: "Inventory",
    items: [
      { to: "/stock", label: "Stock On Hand", icon: Package },
    ],
  },
  {
    label: "Master",
    items: [
      { to: "/vendors", label: "Vendors", icon: UsersThree },
    ],
  },
  {
    label: "Admin",
    items: [
      { to: "/admin/review", label: "Admin Review", icon: ShieldWarning, adminOnly: true },
      { to: "/admin/budget-revisions", label: "Budget Revisions", icon: Scales, adminOnly: true },
      { to: "/admin/rate-revisions", label: "Rate Revisions", icon: Scales, adminOnly: true },
      { to: "/admin/boq-bulk-link", label: "BOQ Bulk-Link", icon: LinkSimple, adminOnly: true },
      { to: "/admin/activity", label: "Activity Log", icon: ClockCounterClockwise, adminOnly: true },
    ],
  },
];

export default function Layout() {
  const { user, logout, isAdmin, isViewer, canWrite } = useAuth();
  const { projects, activeProjectId, activeProject, setActiveProjectId, loading: projectsLoading } = useProject();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="w-64 border-r border-neutral-200 bg-white flex flex-col" data-testid="sidebar">
        <div className="px-5 py-4 border-b border-neutral-200">
          <Link to="/" className="flex items-center gap-2.5 mb-3" data-testid="brand-logo">
            <div className="h-8 w-8 bg-primary flex items-center justify-center">
              <Buildings size={18} weight="bold" color="white" />
            </div>
            <div>
              <div className="font-display text-base font-bold leading-tight tracking-tight">RRL PO Management</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500">Powered by marketly.tech</div>
            </div>
          </Link>

          {/* Project switcher */}
          {projectsLoading ? (
            <div data-testid="project-switcher-loading">
              <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-400 mb-1 px-0.5">Active Project</div>
              <div className="h-10 rounded-sm border border-neutral-200 bg-neutral-50 animate-pulse" />
            </div>
          ) : projects.length > 0 && (
            <div data-testid="project-switcher">
              <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-400 mb-1 px-0.5">Active Project</div>
              <Select value={activeProjectId || ""} onValueChange={setActiveProjectId}>
                <SelectTrigger
                  className="rounded-sm h-10 border-l-[3px]"
                  style={{ borderLeftColor: activeProject?.color || "#0047FF" }}
                  data-testid="project-switcher-trigger"
                >
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`project-option-${p.code}`}>
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block h-2 w-2 rounded-full"
                          style={{ backgroundColor: p.color || "#0047FF" }}
                        />
                        <span>{p.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {isViewer && (
          <div
            className="mx-3 mt-3 px-3 py-2 rounded-sm bg-amber-50 border border-amber-200 text-[11px] uppercase tracking-wider text-amber-800 font-medium"
            data-testid="viewer-readonly-banner"
          >
            Read-only access
          </div>
        )}

        <nav className="flex-1 px-3 py-4 space-y-4 overflow-y-auto">
          {navSections.map((section) => {
            const items = section.items.filter((i) => {
              if (i.adminOnly && !isAdmin) return false;
              if (i.writeOnly && !canWrite) return false;
              return true;
            });
            if (items.length === 0) return null;
            return (
              <div key={section.label}>
                <div className="px-3 mb-1.5 text-[10px] uppercase tracking-[0.18em] text-neutral-400 font-medium">
                  {section.label}
                </div>
                <div className="space-y-0.5">
                  {items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                            isActive
                              ? "bg-primary/5 text-primary font-medium border-l-2 border-primary pl-[10px]"
                              : "text-neutral-700 hover:bg-neutral-100"
                          }`
                        }
                      >
                        <Icon size={17} weight="regular" />
                        <span>{item.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="border-t border-neutral-200 p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-9 w-9 rounded-sm bg-neutral-900 text-white flex items-center justify-center font-display text-sm font-bold">
              {(user?.name || "U").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-[11px] uppercase tracking-wider text-neutral-500">{user?.role}</div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full rounded-sm justify-start gap-2"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <SignOut size={16} />
            <span>Logout</span>
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
